import {
	createLog,
	LINE_ITEM_CODES,
	LogSeverity,
	OPERATIONS_SECTIONS,
	type PickupServiceConfig,
	type PickupServiceConfigDtoInput,
	type PickupServiceConfigDtoOutput,
	pickupServiceConfigDtoSchema,
	PricingModel,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdatePickupService } from '../../../api/configuration/services/useUpdatePickupService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): PickupServiceConfigDtoInput {
	const pickupConfig = servicesProvider?.config?.[Services.PICKUP] as
		| PickupServiceConfig
		| undefined;

	if (!pickupConfig?.pricing?.length) {
		return {
			supported: false,
			pricing: [
				{
					model: PricingModel.FLAT_FEE,
					modelDetails: {
						flatFee: 0,
					},
				},
			],
		};
	}

	const firstPricing = pickupConfig.pricing[0];
	return {
		supported: pickupConfig.supported ?? false,
		pricing: [firstPricing],
	};
}

export function PickupServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.PICKUP);
	const updatePickupServiceMutation = useUpdatePickupService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: PickupServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		// Transform DTO to API payload format
		const dataToSubmit = {
			supported: data.supported,
			pricing: [
				{
					...data.pricing[0],
					code: LINE_ITEM_CODES.PICKUP_FEE,
					feeDescription: 'Pickup fee',
				},
			],
		};

		try {
			await updatePickupServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update pickup service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{ group: SETTINGS_GROUPS.OPERATIONS, section: OPERATIONS_SECTIONS.PICKUP }}
			form={{
				schema: pickupServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Pickup'
			description='Set the default pickup fee charged to buyers at checkout when they choose to pick up their vehicle.'
			formTitle='Pickup pricing'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[
					PricingModel.FLAT_FEE,
					PricingModel.STATE_BASED_FEE,
					PricingModel.VEHICLE_TYPE_CC,
				]}
				modelLabelText='Pickup Fee'
				modelDescriptionText='Fee charged to the buyer at checkout for pickup. This can be customized per vehicle.'
			/>
		</FormSection>
	);
}
